/**
 * SOR Selector Component
 * Modal with search, server-side pagination, multi-select, DataGrid
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '@/services/interceptor';
import DataGrid from '@/modules/utility/ds-wms/components/WmsDataGrid';
import { TableSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';

export interface SORItem {
  sor_itm_id: number;
  sor_itm_cd: string;
  sor_itm_nm: string;
  sor_itm_dscn_tx?: string;
  sor_ctgry_cd: string;
  uom_cd: string;
  uom_nm?: string;
  uom_grp?: string;
  rate_at: number;
  effctv_frm_dt?: string;
  effctv_to_dt?: string;
  // Where in the Schedule of Rates this item sits: which year's SOR, and which SOR type
  // (Buildings / Roads / PHE / Electrical / Irrigation / Urban Greenery). Carried through
  // to the estimate line so a saved BOQ can say which sanctioned rate it was taken from.
  sor_id?: number | null;
  sor_yr_cd?: string;
  sor_type_id?: number | null;
  sor_type_cd?: string;
  sor_type_nm?: string;
}

interface SorYear { sor_id: number; sor_yr_cd: string; sor_nm?: string }
interface SorType { sor_type_id: number; sor_type_cd: string; sor_type_nm: string }

interface SORSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (items: SORItem[]) => void;
  excludeIds?: number[];
}

export default function SORSelector({ isOpen, onClose, onSelect, excludeIds = [] }: SORSelectorProps) {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<SORItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Map<number, SORItem>>(new Map());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [effFromDt, setEffFromDt] = useState('');
  const [effToDt, setEffToDt] = useState('');
  // SOR Year -> SOR Type -> SOR Item. Both narrow the list; neither is required, because
  // an officer who knows the item code should still be able to search across everything.
  const [sorYears, setSorYears] = useState<SorYear[]>([]);
  const [sorTypes, setSorTypes] = useState<SorType[]>([]);
  const [sorId, setSorId] = useState('');
  const [sorTypeId, setSorTypeId] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadItems = useCallback(async (searchText: string, pageNum: number) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('ds-wms/sor-items/picker', {
        params: {
          search: searchText || undefined, page: pageNum, limit: 30,
          effFrom: effFromDt || undefined, effTo: effToDt || undefined,
          sorId: sorId || undefined, sorTypeId: sorTypeId || undefined
        }
      });
      setItems(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 0);
    } catch (e) { console.error('SOR load failed:', e); }
    finally { setLoading(false); }
    // These deps were previously `[]`, which froze the callback around the initial empty
    // filter values — so Effective From / Effective To were read as '' on every call and
    // the Filter button did nothing at all. Listing them makes the filters actually reach
    // the API, which the two new SOR filters depend on as well.
  }, [effFromDt, effToDt, sorId, sorTypeId]);

  // SOR Year and SOR Type masters. Loaded once per open; failure leaves the dropdowns
  // empty rather than blocking the picker, which still works on search alone.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [yrs, tps] = await Promise.all([
          axiosInstance.get('ds-wms/masters/sor-years'),
          axiosInstance.get('ds-wms/masters/sor-types')
        ]);
        if (cancelled) return;
        setSorYears(yrs.data?.data || []);
        setSorTypes(tps.data?.data || []);
      } catch (e) { if (!cancelled) console.error('SOR masters load failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setPage(1); loadItems(search, 1); }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [search, isOpen]);

  // Any filter change re-queries immediately — a cascade the user has to confirm with a
  // separate button is a cascade people forget to press. The Filter button stays as a
  // manual re-run; it is no longer the only thing that applies a filter.
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    loadItems(search, 1);
  }, [sorId, sorTypeId, effFromDt, effToDt]);

  useEffect(() => { if (isOpen && page > 1) loadItems(search, page); }, [page]);

  useEffect(() => {
    if (isOpen) {
      setSearch(''); setSelected(new Map()); setPage(1);
      setSorId(''); setSorTypeId(''); setEffFromDt(''); setEffToDt('');
      loadItems('', 1);
    }
  }, [isOpen]);

  const toggleSelect = (item: SORItem) => {
    if (excludeIds.includes(item.sor_itm_id)) return;
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(item.sor_itm_id)) next.delete(item.sor_itm_id);
      else next.set(item.sor_itm_id, item);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const available = items.filter(i => !excludeIds.includes(i.sor_itm_id));
    setSelected(prev => {
      const next = new Map(prev);
      const allSelected = available.every(i => next.has(i.sor_itm_id));
      if (allSelected) available.forEach(i => next.delete(i.sor_itm_id));
      else available.forEach(i => next.set(i.sor_itm_id, i));
      return next;
    });
  };

  const handleConfirm = () => { onSelect(Array.from(selected.values())); onClose(); };

  if (!isOpen) return null;

  const allPageSelected = items.length > 0 && items.filter(i => !excludeIds.includes(i.sor_itm_id)).every(i => selected.has(i.sor_itm_id));

  const dataWithSelection = items.map((item, idx) => ({
    ...item,
    sno: (page - 1) * 30 + idx + 1,
    _selected: selected.has(item.sor_itm_id),
    _excluded: excludeIds.includes(item.sor_itm_id)
  }));

  const columns = [
    { field: '_select', columnName: '', columnType: 'text' as const, width: '40px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => (
        <input type="checkbox" checked={row._selected} disabled={row._excluded}
          onChange={() => toggleSelect(row)}
          className={`rounded border-gray-300 text-indigo-600 w-4 h-4 ${row._excluded ? 'opacity-30' : 'cursor-pointer'}`} />
      )
    },
    { field: 'sno', columnName: 'S.No', columnType: 'number' as const, width: '50px', visible: true, alignment: 'center' as const },
    { field: 'sor_itm_cd', columnName: 'SOR Code', columnType: 'text' as const, width: '110px', visible: true, filter: true,
      cellRenderer: (row: any) => <span className="font-mono text-xs font-semibold text-indigo-700">{row.sor_itm_cd}</span>
    },
    { field: 'sor_itm_nm', columnName: 'Description', columnType: 'text' as const, width: '250px', visible: true, filter: true,
      cellRenderer: (row: any) => (
        <div className="min-w-0">
          <p className="text-xs truncate" title={row.sor_itm_nm}>{row.sor_itm_nm}</p>
          {row.sor_itm_dscn_tx && <p className="text-[10px] text-gray-400 truncate" title={row.sor_itm_dscn_tx}>{row.sor_itm_dscn_tx}</p>}
        </div>
      )
    },
    { field: 'sor_yr_cd', columnName: 'SOR Year', columnType: 'text' as const, width: '80px', visible: true, alignment: 'center' as const,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-600">{row.sor_yr_cd || '-'}</span>
    },
    { field: 'sor_type_nm', columnName: 'SOR Type', columnType: 'text' as const, width: '120px', visible: true,
      cellRenderer: (row: any) => (
        row.sor_type_nm
          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{row.sor_type_nm}</span>
          : <span className="text-[10px] text-gray-400">-</span>
      )
    },
    { field: 'sor_ctgry_cd', columnName: 'Section', columnType: 'text' as const, width: '120px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{row.sor_ctgry_cd || '-'}</span>
    },
    { field: 'uom_grp', columnName: 'Unit Group', columnType: 'text' as const, width: '80px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.uom_grp || '-'}</span>
    },
    { field: 'uom_cd', columnName: 'UOM', columnType: 'text' as const, width: '60px', visible: true, alignment: 'center' as const },
    { field: 'rate_at', columnName: 'Rate (₹)', columnType: 'number' as const, width: '90px', visible: true, alignment: 'right' as const,
      cellRenderer: (row: any) => <span className="font-mono text-xs font-semibold">₹{row.rate_at?.toLocaleString('en-IN')}</span>
    },
    { field: 'effctv_frm_dt', columnName: 'Effective From', columnType: 'text' as const, width: '90px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.effctv_frm_dt || '-'}</span>
    },
    { field: 'effctv_to_dt', columnName: 'Effective To', columnType: 'text' as const, width: '90px', visible: true,
      cellRenderer: (row: any) => <span className="text-[10px] text-gray-500">{row.effctv_to_dt || '-'}</span>
    },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className={`relative bg-white shadow-2xl flex flex-col ${isFullscreen ? 'w-full h-full rounded-none' : 'rounded-xl w-full max-w-5xl max-h-[85vh]'}`}>
        {/* Header */}
        <div className={`${WMS_POPUP_HEADER} ${isFullscreen ? '' : 'rounded-t-xl'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-file-list-3-line text-base text-white/90"></i>
            <div className="min-w-0">
              <h2 className={WMS_POPUP_TITLE}>Select SOR Items</h2>
              <p className={WMS_POPUP_SUBTITLE}>{total} items available | {selected.size} selected</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setIsFullscreen(!isFullscreen)} className={WMS_POPUP_CLOSE} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
            </button>
            <button onClick={onClose} className={WMS_POPUP_CLOSE}><i className="ri-close-line text-lg"></i></button>
          </div>
        </div>

        {/* Search + Date Filters + Select All */}
        <div className="px-5 py-3 border-b flex items-center gap-2">
          <label className="flex items-center gap-1.5 flex-shrink-0">
            <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
              className="rounded border-gray-300 text-indigo-600 w-4 h-4" />
            <span className="text-[10px] text-gray-500">All</span>
          </label>
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SOR code, description..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" autoFocus />
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div>
              <label className="text-[9px] text-gray-400 block">SOR Year</label>
              <select value={sorId} onChange={(e) => setSorId(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-28 bg-white">
                <option value="">All years</option>
                {sorYears.map(y => (
                  <option key={y.sor_id} value={y.sor_id}>{y.sor_yr_cd}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-gray-400 block">SOR Type</label>
              <select value={sorTypeId} onChange={(e) => setSorTypeId(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-36 bg-white">
                <option value="">All types</option>
                {sorTypes.map(t => (
                  <option key={t.sor_type_id} value={t.sor_type_id}>{t.sor_type_nm}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-gray-400 block">Effective From</label>
              <input type="date" value={effFromDt} onChange={(e) => setEffFromDt(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-32" />
            </div>
            <div>
              <label className="text-[9px] text-gray-400 block">Effective To</label>
              <input type="date" value={effToDt} onChange={(e) => setEffToDt(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs w-32" />
            </div>
            <button onClick={() => { setPage(1); loadItems(search, 1); }}
              className="mt-3 px-3 py-1.5 bg-indigo-600 text-white rounded text-xs font-medium hover:bg-indigo-700 flex items-center gap-1">
              <i className="ri-filter-line"></i> Filter
            </button>
            {(effFromDt || effToDt || sorId || sorTypeId) && (
              <button onClick={() => { setEffFromDt(''); setEffToDt(''); setSorId(''); setSorTypeId(''); setPage(1); }}
                className="mt-3 px-2 py-1.5 text-gray-500 hover:text-gray-700 text-xs" title="Clear filters">
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* DataGrid */}
        <div className="flex-1 overflow-auto">
          {loading && items.length === 0 ? (
            <TableSkeleton />
          ) : items.length === 0 ? (
            // An empty grid with working filters above it reads as "your filter is too
            // narrow". Usually it is not: the rate-item master has no rows at all yet.
            // Say which of the two it is rather than leaving the officer to guess.
            <div className="p-12 text-center">
              <i className="ri-price-tag-3-line text-4xl text-gray-300 block mb-3"></i>
              {(search || sorId || sorTypeId || effFromDt || effToDt) ? (
                <>
                  <p className="text-sm text-gray-600 font-medium">No SOR item matches these filters</p>
                  <p className="text-xs text-gray-400 mt-1">Clear the year, type or date filters and search again.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 font-medium">No Schedule of Rates items have been loaded yet</p>
                  <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                    The SOR rate schedule has to be loaded into the rate-item master before items
                    can be picked here. Until then, add estimate lines manually — they save
                    normally, just without an SOR reference.
                  </p>
                </>
              )}
            </div>
          ) : (
            <DataGrid
              variant="embedded"
              dataSource={dataWithSelection}
              columns={columns}
              manualPagination={true}
              pageCount={totalPages}
              gridConfig={{ pagination: false, enableSearchPanel: false, enableColumnResizing: true }}
              onRowClick={(row: any) => !row._excluded && toggleSelect(row)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50 rounded-b-xl">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Page {page} of {totalPages} ({total} items)</span>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-40"><i className="ri-arrow-left-s-line"></i></button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 border rounded hover:bg-gray-100 disabled:opacity-40"><i className="ri-arrow-right-s-line"></i></button>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full">
                {selected.size} item{selected.size !== 1 ? 's' : ''} selected
              </span>
            )}
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100">Cancel</button>
            <button onClick={handleConfirm} disabled={selected.size === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
              <i className="ri-check-line"></i> Add {selected.size} Item{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
